import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import type { MentionEntry, MentionStaffMember } from '~/components/ui/MentionDropdown';
import {
  MENTION_SPAN_CLASS,
  buildEditableChildren,
  findActiveMentionQuery,
  readDisplayText,
  serializeEditable,
} from '~/utils/mention-dom';

type Params = {
  editableRef: React.RefObject<HTMLDivElement | null>;
  staff: MentionStaffMember[];
  value: string;
  initialMentions?: MentionEntry[];
  onChange: (displayText: string) => void;
  onSerializedChange: (serialized: string) => void;
  /** Demo Builder: open the mention list on mount. Default off. */
  openMentionsOnMount?: boolean;
};

function filterStaff(staff: MentionStaffMember[], query: string): MentionStaffMember[] {
  if (!query) return staff.slice(0, 10);
  const lower = query.toLowerCase();
  return staff
    .filter(
      s => s.firstName.toLowerCase().startsWith(lower) || s.lastName.toLowerCase().startsWith(lower)
    )
    .slice(0, 10);
}

function formatDisplayName(m: MentionStaffMember): string {
  return m.degree ? `${m.firstName} ${m.lastName}, ${m.degree}` : `${m.firstName} ${m.lastName}`;
}

function reconstructSerialized(displayText: string, mentions: MentionEntry[]): string {
  let s = displayText;
  for (const m of mentions) {
    s = s.split(m.displayText).join(`<@${m.id}|${m.displayText.slice(1)}>`);
  }
  return s;
}

function locateAtQuery(root: HTMLElement): { node: Text; at: number; end: number } | null {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let current: Node | null;
  while ((current = walker.nextNode())) {
    const text = current as Text;
    for (let i = 0; i < text.data.length; i += 1) {
      if (text.data[i] !== '@') continue;
      if (i > 0 && !/\s/.test(text.data[i - 1])) continue;
      const found = findActiveMentionQuery(text.data, i + 1);
      if (found) {
        return { node: text, at: found.at, end: found.at + 1 + found.query.length };
      }
    }
  }
  return null;
}

export function useMentionEditable({
  editableRef,
  staff,
  value,
  initialMentions,
  onChange,
  onSerializedChange,
  openMentionsOnMount = false,
}: Params) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<MentionStaffMember[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<{ node: Text; at: number; end: number } | null>(null);
  const lastEmittedDisplayRef = useRef<string | null>(null);
  const mentionsRef = useRef<MentionEntry[]>(initialMentions ?? []);

  const emit = useCallback(() => {
    const root = editableRef.current;
    if (!root) return;
    const serialized = serializeEditable(root);
    const display = readDisplayText(root);
    // Normalize a stray lone <br>/whitespace to truly empty so the CSS
    // :empty placeholder reappears (serialized already '' so submission is safe).
    if (serialized === '' && root.childNodes.length > 0) {
      root.replaceChildren();
    }
    lastEmittedDisplayRef.current = display;
    onSerializedChange(serialized);
    onChange(display);
  }, [editableRef, onChange, onSerializedChange]);

  useLayoutEffect(() => {
    const root = editableRef.current;
    if (!root) return;
    // `value` is DISPLAY text. If it equals what we last emitted, this render
    // was caused by our own DOM-origin edit (typing or dropdown insert) — never
    // re-seed (that would wipe runtime-inserted mention spans and the caret).
    if (value === lastEmittedDisplayRef.current) return;
    // External change (clear-after-send, edit-open): rebuild from the wire form.
    const wire = reconstructSerialized(value, initialMentions ?? []);
    root.replaceChildren(...buildEditableChildren(wire, document));
    lastEmittedDisplayRef.current = value;
    mentionsRef.current = initialMentions ?? [];
    onSerializedChange(wire);
  }, [value, initialMentions, editableRef, onSerializedChange]);

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSuggestions([]);
    triggerRef.current = null;
  }, []);

  const detectTrigger = useCallback(() => {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return closeDropdown();
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE || !editableRef.current?.contains(node)) {
      return closeDropdown();
    }
    const textNode = node as Text;
    const offset = range.startOffset;
    const found = findActiveMentionQuery(textNode.data, offset);
    if (!found) return closeDropdown();
    const filtered = filterStaff(staff, found.query);
    if (filtered.length === 0) return closeDropdown();
    triggerRef.current = { node: textNode, at: found.at, end: offset };
    setSuggestions(filtered);
    setActiveIndex(0);
    setIsOpen(true);
  }, [staff, closeDropdown, editableRef]);

  const onInput = useCallback(() => {
    emit();
    detectTrigger();
  }, [emit, detectTrigger]);

  const placeCaretAfter = useCallback((node: Node) => {
    const after = document.createRange();
    after.setStartAfter(node);
    after.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(after);
  }, []);

  const selectMention = useCallback(
    (member: MentionStaffMember) => {
      const root = editableRef.current;
      if (!root) return;

      const writeMention = (trig: { node: Text; at: number; end: number } | null) => {
        const doc = root.ownerDocument;
        const span = doc.createElement('span');
        span.dataset.mentionId = member.id;
        span.setAttribute('contenteditable', 'false');
        span.className = MENTION_SPAN_CLASS;
        span.textContent = `@${formatDisplayName(member)}`;
        const space = doc.createTextNode(' ');

        if (trig) {
          const range = doc.createRange();
          range.setStart(trig.node, trig.at);
          range.setEnd(trig.node, trig.end);
          range.deleteContents();
          range.insertNode(space);
          range.insertNode(span);
        } else if (root.firstChild) {
          root.insertBefore(space, root.firstChild);
          root.insertBefore(span, space);
        } else {
          root.appendChild(span);
          root.appendChild(space);
        }

        placeCaretAfter(space);
        mentionsRef.current = [
          ...mentionsRef.current.filter(m => m.id !== member.id),
          { id: member.id, displayText: `@${formatDisplayName(member)}` },
        ];
        closeDropdown();
        emit();
      };

      if (!openMentionsOnMount) {
        const trig = triggerRef.current;
        if (!trig) return;
        writeMention(trig);
        return;
      }

      let trig = triggerRef.current;
      if (!trig?.node.isConnected || (trig && !root.contains(trig.node))) {
        trig = locateAtQuery(root);
        triggerRef.current = trig;
      }
      try {
        writeMention(trig);
      } catch {
        writeMention(null);
      }
    },
    [editableRef, closeDropdown, emit, placeCaretAfter, openMentionsOnMount]
  );

  /** Prepend an @mention (e.g. Reply). No-ops the insert if that user is already mentioned. */
  const insertMention = useCallback(
    (member: MentionStaffMember) => {
      const root = editableRef.current;
      if (!root) return;
      root.focus();

      const existing = Array.from(root.querySelectorAll('[data-mention-id]')).find(
        el => (el as HTMLElement).dataset.mentionId === member.id
      );
      if (existing) {
        const next = existing.nextSibling;
        placeCaretAfter(next && next.nodeType === Node.TEXT_NODE ? next : existing);
        closeDropdown();
        return;
      }

      const doc = root.ownerDocument;
      const span = doc.createElement('span');
      span.dataset.mentionId = member.id;
      span.setAttribute('contenteditable', 'false');
      span.className = MENTION_SPAN_CLASS;
      span.textContent = `@${formatDisplayName(member)}`;
      const space = doc.createTextNode(' ');

      if (root.firstChild) {
        root.insertBefore(space, root.firstChild);
        root.insertBefore(span, space);
      } else {
        root.appendChild(span);
        root.appendChild(space);
      }

      placeCaretAfter(space);

      mentionsRef.current = [
        ...mentionsRef.current.filter(m => m.id !== member.id),
        { id: member.id, displayText: `@${formatDisplayName(member)}` },
      ];
      closeDropdown();
      emit();
    },
    [editableRef, closeDropdown, emit, placeCaretAfter]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setActiveIndex(p => (p + 1) % suggestions.length);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setActiveIndex(p => (p - 1 + suggestions.length) % suggestions.length);
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          if (suggestions[activeIndex]) selectMention(suggestions[activeIndex]);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          closeDropdown();
          return;
        }
      }
    },
    [isOpen, suggestions, activeIndex, selectMention, closeDropdown]
  );

  const insertAtCursor = useCallback(
    (text: string) => {
      const root = editableRef.current;
      if (!root) return;
      root.focus();
      const sel = window.getSelection();
      if (!sel) return;

      let range: Range;
      if (sel.rangeCount === 0) {
        range = document.createRange();
        range.selectNodeContents(root);
        range.collapse(false);
        sel.addRange(range);
      } else {
        range = sel.getRangeAt(0);
      }

      range.deleteContents();
      const parts = text.split('\n');
      const frag = document.createDocumentFragment();
      parts.forEach((part, i) => {
        if (i > 0) frag.appendChild(document.createElement('br'));
        if (part) frag.appendChild(document.createTextNode(part));
      });
      const last = frag.lastChild;
      range.insertNode(frag);
      if (last) {
        const after = document.createRange();
        // Keep the caret inside the text node so detectTrigger can read @query.
        if (last.nodeType === Node.TEXT_NODE) {
          after.setStart(last, (last as Text).length);
        } else {
          after.setStartAfter(last);
        }
        after.collapse(true);
        sel.removeAllRanges();
        sel.addRange(after);
      }

      emit();
      if (text === '@') detectTrigger();
    },
    [editableRef, emit, detectTrigger]
  );

  const triggerMention = useCallback(() => {
    insertAtCursor('@');
  }, [insertAtCursor]);

  const didAutoOpenMentions = useRef(false);
  useEffect(() => {
    if (!openMentionsOnMount || didAutoOpenMentions.current || staff.length === 0) return;
    const root = editableRef.current;
    if (!root) return;
    didAutoOpenMentions.current = true;

    root.focus({ preventScroll: true });
    const first = root.firstChild;
    if (first?.nodeType === Node.TEXT_NODE && (first as Text).data.startsWith('@')) {
      const after = document.createRange();
      after.setStart(first, 1);
      after.collapse(true);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(after);
      detectTrigger();
      return;
    }

    const doc = root.ownerDocument;
    const at = doc.createTextNode('@');
    if (root.firstChild) {
      const space = doc.createTextNode(' ');
      root.insertBefore(space, root.firstChild);
      root.insertBefore(at, space);
    } else {
      root.appendChild(at);
    }
    const after = doc.createRange();
    after.setStart(at, 1);
    after.collapse(true);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(after);
    detectTrigger();
  }, [openMentionsOnMount, staff, editableRef, detectTrigger]);

  return {
    isOpen,
    suggestions,
    activeIndex,
    onInput,
    onKeyDown,
    selectMention,
    insertMention,
    closeDropdown,
    insertAtCursor,
    triggerMention,
  };
}
